'use client'

import React from 'react'
import Link from 'next/link'
import Image from 'next/image'
import {
  ArrowUpRight,
  Rocket,
  Briefcase,
  Sparkles,
  Building2,
  GraduationCap,
  Heart,
  TrendingUp,
  type LucideIcon,
} from 'lucide-react'
import { motion } from 'framer-motion'
import type { Media, Persona } from '@/payload-types'
import { trackPersonaCardClicked } from '../../lib/analytics'
import { getPersonaHref } from '@/lib/persona-paths'
import { getMediaUrl } from '../../lib/media'

const personaIcons: Record<string, LucideIcon> = {
  founders: Rocket,
  professionals: Briefcase,
  'extraordinary-individuals': Sparkles,
  employers: Building2,
  students: GraduationCap,
  families: Heart,
  investors: TrendingUp,
  innovators: Sparkles,
}

interface PersonaCardsProps {
  personas?: (string | Persona)[] | null
  /** Homepage trims the grid; list pages show every persona. */
  variant?: 'home' | 'list'
}

export default function PersonaCards({ personas, variant = 'home' }: PersonaCardsProps) {
  if (!personas || personas.length === 0) {
    return null
  }

  // Filter out any string IDs and only keep full Persona objects
  const personaList = personas.filter((persona) => typeof persona !== 'string') as Persona[]

  if (personaList.length === 0) {
    return null
  }

  const gridPersonas =
    variant === 'list'
      ? personaList
      : personaList.filter((persona) => persona.slug !== 'families').slice(0, 7)

  return (
    <section className={variant === 'list' ? 'px-0' : 'pt-12 md:pt-20 px-6 md:px-12'}>
      <div className={variant === 'list' ? '' : 'max-w-380 mx-auto'}>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
          {gridPersonas.map((persona) => {
            const featuredImage = persona.featuredImage as Media | null | undefined
            const imageUrl = getMediaUrl(featuredImage, 'card')
            const Icon = persona.slug ? personaIcons[persona.slug] : undefined

            return (
              <motion.div
                key={persona.id}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{
                  once: true,
                  // Animate each card as it approaches the viewport (not after 40% of the whole grid)
                  amount: 0.12,
                  margin: '0px 0px 120px 0px',
                }}
                transition={{ duration: 0.45, ease: [0.25, 0.1, 0.25, 1] }}
              >
                <Link
                  href={getPersonaHref(persona.slug)}
                  onClick={() => trackPersonaCardClicked(persona.slug || '')}
                  className="cgroup relative overflow-hidden rounded-2xl bg-gray-800 text-white transition-all h-[280px] md:h-[320px] flex duration-300 group"
                >
                  {/* Background Image - optimized via next/image */}
                  {imageUrl && (
                    <Image
                      src={imageUrl}
                      alt={persona.name || ''}
                      fill
                      sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, 33vw"
                      className="object-cover object-center"
                    />
                  )}

                  {/* Gradient overlay for text readability */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-black/20 transition-all" />

                  {/* Content */}
                  <div className="relative z-10 p-5 md:p-6 flex flex-col justify-end w-full">
                    <div>
                      <h2
                        className="text-xl md:text-2xl font-medium mb-1 flex items-center gap-2"
                        style={{
                          textShadow: '2px 2px 12px rgba(0, 0, 0, 0.6)',
                        }}
                      >
                        {Icon && <Icon className="w-5 h-5 md:w-6 md:h-6 shrink-0 text-white/90" />}
                        {persona.name}
                      </h2>
                      {persona.shortDescription && (
                        <p className="text-sm md:text-base leading-relaxed text-white/95 mb-4">
                          {persona.shortDescription}
                        </p>
                      )}
                    </div>
                    <button className="px-6 py-2.5 rounded-full text-sm font-medium inline-flex items-center gap-2 w-fit bg-transparent text-white border border-white/30 group-hover:bg-white group-hover:text-gray-900 transition-all backdrop-blur-sm">
                      Explore Options
                      <ArrowUpRight className="w-4 h-4" />
                    </button>
                  </div>
                </Link>
              </motion.div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
